import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/common/accordion'
import { Button } from '@/components/common/button'
import { ArrowIcon } from '@/components/icons/arrow'
import { LoadingIcon } from '@/components/icons/loading'
import type { CrosschainQuoteResponse } from '@/queries/crosschainQuote.query'
import { cn } from '@/utils/cn'
import { fromUnixTimestamp } from '@vaultsfyi/common/utils'
import { formatPreciseTokenValue } from '@vaultsfyi/common/utils'
import { useEffect, useMemo, useState } from 'react'

interface Props {
  inputAmount: string
  inputDecimals: number
  inputSymbol: string
  isFetching: boolean
  quote?: CrosschainQuoteResponse | null
  receivedTokenSymbol: string
  underlyingSymbol: string
  onQuoteExpired?: () => void
}

const QUOTE_BREAKDOWN_ID = 'quote-breakdown' as const

export const CrosschainQuoteBreakdown = ({
  isFetching,
  quote,
  inputAmount,
  inputDecimals,
  inputSymbol,
  receivedTokenSymbol,
  underlyingSymbol,
  onQuoteExpired,
}: Props) => {
  const [openItems, setOpenItems] = useState<string[]>([])

  const animationDuration = useMemo(() => {
    if (!quote?.expirationTimestamp) return null

    const now = Date.now()
    const expirationMs = fromUnixTimestamp(quote.expirationTimestamp)
    const remaining = expirationMs - now

    return remaining > 0 ? remaining : null
  }, [quote?.expirationTimestamp])

  useEffect(() => {
    if (isFetching || quote) {
      setOpenItems([QUOTE_BREAKDOWN_ID])
    } else {
      setOpenItems([])
    }
  }, [isFetching, quote])

  useEffect(() => {
    if (!quote?.expirationTimestamp || !animationDuration) return

    const expirationTimeout = setTimeout(() => {
      onQuoteExpired?.()
    }, animationDuration)

    return () => {
      clearTimeout(expirationTimeout)
    }
  }, [animationDuration, onQuoteExpired, quote?.expirationTimestamp])

  if (!isFetching && !quote) {
    return null
  }

  return (
    <Accordion value={openItems} onValueChange={setOpenItems} type="multiple">
      <AccordionItem className={cn('group overflow-hidden rounded-xl border')} value={QUOTE_BREAKDOWN_ID}>
        <AccordionTrigger asChild>
          <Button
            variant="default"
            size="default"
            className="flex h-12 w-full items-center justify-between gap-2 px-3 py-0.5 font-bold text-heading-foreground text-sm"
          >
            {isFetching ? (
              <div className="flex items-center gap-2">
                <LoadingIcon size={24} className="animate-spin" />
                <span>Calculating quote...</span>
              </div>
            ) : (
              <>
                <span>Quote details</span>
                <div className="group-data-[state=open]:-scale-y-100 transition-transform">
                  <ArrowIcon direction="down" />
                </div>
              </>
            )}
          </Button>
        </AccordionTrigger>
        <AccordionContent>
          {!isFetching && animationDuration !== null && animationDuration > 0 && (
            <div
              className="h-1 w-full origin-left animate-loader-scale bg-accent"
              style={{
                animationDuration: `${animationDuration}ms`,
                animationIterationCount: '1',
                animationTimingFunction: 'linear',
                animationFillMode: 'forwards',
              }}
              key={quote?.expirationTimestamp}
            />
          )}
          {!isFetching && quote && (
            <>
              <p className="flex items-center justify-between gap-x-2 gap-y-0.5 border-y bg-border px-3 py-2 text-heading-foreground">
                <span>You send:</span>
                <span>
                  {formatPreciseTokenValue({
                    value: inputAmount,
                    decimals: inputDecimals,
                  })}{' '}
                  {inputSymbol}
                </span>
              </p>

              <ul className="flex flex-col gap-2 px-3 py-2 text-xs">
                {quote.fees.map((fee, idx) => (
                  <li key={idx} className="flex items-center justify-between">
                    <span className="text-body-foreground">
                      <span className="capitalize">{fee.type}</span> fee
                    </span>
                    <span className="text-heading-foreground">
                      {formatPreciseTokenValue({
                        value: fee.amount,
                        decimals: fee.token.decimals,
                      })}{' '}
                      {inputSymbol}
                    </span>
                  </li>
                ))}
              </ul>

              <p className="flex justify-between gap-x-2 gap-y-0.5 border-y bg-accent/10 px-3 py-2 font-bold text-accent">
                <span>You receive:</span>
                <span className="flex flex-col items-end gap-0.5">
                  <span>
                    {formatPreciseTokenValue({
                      value: quote.receivedPosition.amount,
                      decimals: quote.receivedPosition.decimals,
                    })}{' '}
                    {receivedTokenSymbol}
                  </span>
                  <span className="font-normal text-body-foreground text-xs">
                    ≈{' '}
                    {formatPreciseTokenValue({
                      value: quote.receivedAssetValue.amount,
                      decimals: quote.receivedAssetValue.decimals,
                    })}{' '}
                    {underlyingSymbol}
                  </span>
                </span>
              </p>

              <div
                className={cn(
                  'flex px-3 py-2 text-xs',
                  quote.totalCost.length > 1 ? 'flex-col gap-0.5' : 'items-center justify-between'
                )}
              >
                <span className="text-body-foreground">Total charge from wallet:</span>
                {quote.totalCost.map((cost, idx) => (
                  <span className="text-heading-foreground" key={idx}>
                    {formatPreciseTokenValue({
                      value: cost.amount,
                      decimals: cost.decimals,
                    })}{' '}
                    {inputSymbol}
                  </span>
                ))}
              </div>
            </>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
